import { useMemo } from "react";
import { get, filter, includes, isEmpty } from "lodash";
import { useWorkspace } from "src/contexts/global/WorkspaceContext";
import { ENTITY_TYPES } from "src/consts/entityTypes";

/**
 * Returns two sets of tag type configs:
 * - tagTypesConfigForDisplay: for displaying already-applied tags (broad filter)
 * - tagTypesConfigForSelection: for tag creation/selection (strict filter)
 *
 * @param {Object} params
 * @param {string} params.entityType - "person", "group", or "agent"
 * @param {string} [params.visibleIn] - UI component visibility filter (e.g. "Directory", "Teamform")
 * @param {string} [params.groupType] - Group type string, only applicable when entityType is "group"
 * @param {string[]} [params.tagTypesFilter] - Optional allowlist of tag type ids to include
 * @param {string[]} [params.excludingTagTypes] - Optional list of tag type ids to exclude from display config only
 * @returns {{ tagTypesConfigForDisplay: Array, tagTypesConfigForSelection: Array }}
 */
const useTagTypes = ({
  entityType,
  visibleIn,
  groupType,
  tagTypesFilter,
  excludingTagTypes,
} = {}) => {
  const workspace = useWorkspace();
  const tagConfigs = get(workspace, "config.tagConfig", {});

  // Base filtering: only isActive and visibleIn.
  // Intentionally does NOT filter by entityType or groupType so that
  // previously applied tags remain visible even if the admin later changes
  // the config (e.g. disallows groups from having a tag type).
  const baseTagTypesConfig = useMemo(() => {
    const configs = Object.values(tagConfigs);

    return filter(configs, (config) => {
      // Filter out inactive tag types
      if (!config.isActive) {
        return false;
      }

      // Filter out group association tag types
      if (config.isGroupAssociation) {
        return false;
      }

      // Filter by visibleIn
      // We check the top-level visibleIn which uses enum values matching TAGS_UI_COMPONENTS
      if (visibleIn && !includes(config.visibleIn, visibleIn)) {
        return false;
      }

      return true;
    });
  }, [tagConfigs, visibleIn]);

  // Display filtering: base config with excludingTagTypes removed.
  // These excluded types should not appear in the tags display at all.
  const tagTypesConfigForDisplay = useMemo(() => {
    if (isEmpty(excludingTagTypes)) {
      return baseTagTypesConfig;
    }
    return filter(
      baseTagTypesConfig,
      (config) => !includes(excludingTagTypes, config.id)
    );
  }, [baseTagTypesConfig, excludingTagTypes]);

  // Selection filtering: applies full backend restrictions for creating/attaching tags.
  // Derives from baseTagTypesConfig (not display) so excludingTagTypes does not
  // affect which tag types are available for selection.
  const tagTypesConfigForSelection = useMemo(() => {
    return filter(baseTagTypesConfig, (config) => {
      // Filter by tag types filter
      if (!isEmpty(tagTypesFilter) && !includes(tagTypesFilter, config.id)) {
        return false;
      }

      // Filter by entity type
      if (entityType === ENTITY_TYPES.PERSON && !config.allowPersonToHaveTag) {
        return false;
      }
      if (entityType === ENTITY_TYPES.GROUP && !config.allowGroupToHaveTag) {
        return false;
      }
      if (entityType === ENTITY_TYPES.AGENT && !config.allowAgentToHaveTag) {
        return false;
      }

      // Filter by groupType for group entities
      if (entityType === ENTITY_TYPES.GROUP && groupType) {
        const allowedGroupTypes = get(config, "allowedGroupTypes", []);

        if (!isEmpty(allowedGroupTypes)) {
          const hasAll = allowedGroupTypes.some((gt) => gt.key === "ALL");
          const hasMatch = allowedGroupTypes.some((gt) => gt.key === groupType);

          if (!hasAll && !hasMatch) {
            return false;
          }
        }
      }

      return true;
    });
  }, [baseTagTypesConfig, entityType, groupType, tagTypesFilter]);

  return {
    tagTypesConfigForDisplay,
    tagTypesConfigForSelection,
  };
};

export default useTagTypes;
